import React, { useState, useMemo, Fragment, useEffect } from 'react'
import injectStyles from 'react-jss'
import { useEditSequenceContext } from '../../../context/EditSequenceContext'
import {
  Box,
  Fab,
  Grid,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Select,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import GroupSelect from '../../../../../GroupSelect'
import {
  comparisonMap,
  optionsTriggers,
  conditionalTriggers,
  smallerComparisonMap,
} from '../../../../../../options'
import { styles } from './styles'

const TRIGGER_TYPES = {
  CONDITIONAL: 'CONDITIONAL',
}

const Trigger = ({
  id,
  index,
  trigger,
  classes,
  onDelete,
  handleDrawerToggle,
  isAnyElementBelow,
}) => {
  const {
    editSequenceContextState,
    updateTrigger,
    setErrors,
  } = useEditSequenceContext()
  const { stage, errors } = editSequenceContextState
  const [anchorEl, setAnchorEl] = useState(null)
  const ITEM_HEIGHT = 48
  const open = useMemo(() => Boolean(anchorEl), [anchorEl])
  const comparisons = useMemo(
    () =>
      trigger.triggerType.name === 'BA distributed N flyers'
        ? smallerComparisonMap
        : comparisonMap,
    [trigger.triggerType.name],
  )

  const comparison = useMemo(
    () =>
      trigger.triggerType.conditionType
        ? trigger.triggerType.conditionType.operation
        : null,
    [trigger],
  )

  const triggerList = useMemo(
    () =>
      optionsTriggers.filter(triggerOption =>
        triggerOption.stages.includes(stage),
      ),
    [stage],
  )

  useEffect(() => {
    if (!triggerList.map(t => t.value).includes(trigger.triggerType.name)) {
      updateTrigger({
        ...trigger,
        target_value: '',
        triggerType: { name: 'NONE' },
      })
    }
  }, [triggerList])

  const selectTriggerType = useMemo(
    () =>
      conditionalTriggers.includes(trigger.triggerType.name)
        ? TRIGGER_TYPES.CONDITIONAL
        : null,
    [trigger],
  )

  const triggerConstant = useMemo(() => trigger.target_value || '', [trigger])

  const isCorrectTrigger = useMemo(
    () =>
      trigger.triggerType.name &&
      trigger.triggerType.name != 'NONE' &&
      triggerList.map(t => t.value).includes(trigger.triggerType.name),
    [trigger.triggerType.name, triggerList],
  )

  const isBARating = useMemo(
    () => isCorrectTrigger && trigger.triggerType.name.includes('BA Rating'),
    [isCorrectTrigger, trigger.triggerType.name],
  )

  const wrongRatingConstant = useMemo(
    () =>
      isBARating &&
      (Number(triggerConstant) < 0 || Number(triggerConstant) > 6),
    [isBARating, triggerConstant],
  )

  const wrongConstant = useMemo(
    () =>
      isNaN(Number(triggerConstant)) ||
      Number(triggerConstant) < 0 ||
      Number(triggerConstant) > 10000,
    [triggerConstant],
  )

  const wrongComparison = useMemo(() => comparison === 'null' || !comparison, [
    comparison,
  ])

  const incompleteTrigger = useMemo(
    () =>
      !isCorrectTrigger ||
      !trigger.triggerType.name ||
      wrongRatingConstant ||
      (selectTriggerType === TRIGGER_TYPES.CONDITIONAL &&
        (!triggerConstant || wrongComparison || wrongConstant)),
    [
      isCorrectTrigger,
      trigger.triggerType.name,
      wrongRatingConstant,
      selectTriggerType,
      triggerConstant,
      wrongComparison,
      wrongConstant,
    ],
  )

  useEffect(() => {
    if (incompleteTrigger) {
      setErrors({ type: 'trigger', error: 'Incomplete trigger data' })
    } else if (errors.trigger) {
      setErrors({ type: 'trigger', error: '' })
    }
  }, [incompleteTrigger])

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleComparisonChange = event => {
    updateTrigger({
      ...trigger,
      triggerType: {
        ...trigger.triggerType,
        conditionType: { operation: event.target.value },
      },
    })
  }

  const handleConstantChange = event => {
    const pureValue = event.target.value
    if (pureValue === '') {
      updateTrigger({ ...trigger, target_value: '' })
    } else {
      let newValue = Number(pureValue)
      if (isBARating && !isNaN(newValue)) {
        if (newValue > 6) {
          newValue = 6
        } else if (newValue < 0) {
          newValue = 0
        }

        updateTrigger({ ...trigger, target_value: String(newValue) })
      } else {
        updateTrigger({ ...trigger, target_value: pureValue })
      }
    }
  }

  const handleSelectTrigger = value => {
    updateTrigger({
      ...trigger,
      triggerType: { name: value },
      target_value: '',
    })
  }

  const popupOptions = [
    {
      id: 1,
      label: 'Delete',
      func: id => {
        handleClose()
        onDelete(id)
      },
    },
  ]

  return (
    <Fragment>
      <Grid container direction="column" alignItems="center">
        {index > 0 ? (
          <div>
            <Grid item xs>
              <div className={classes.line} />
            </Grid>
          </div>
        ) : null}
        <Grid item>
          <div className={classes.header}>
            {`Trigger`}
            <div>
              <IconButton onClick={handleClick}>
                <MoreVertIcon style={{ fill: 'white' }} />
              </IconButton>
              <Menu
                id="long-menu"
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                PaperProps={{
                  style: {
                    maxHeight: ITEM_HEIGHT * 4.5,
                    width: '11ch',
                  },
                }}
              >
                {popupOptions.map(item => (
                  <MenuItem onClick={() => item.func(id)}>
                    <div className={classes.popupItem}>{item.label}</div>
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
        </Grid>
        <Grid item>
          <div className={classes.body}>
            <Grid container direction="column">
              <div className={classes.wrapperSelect}>
                <GroupSelect
                  key={`dropdown-${stage}`}
                  label="Trigger"
                  options={triggerList}
                  fontSize={14}
                  onChange={handleSelectTrigger}
                  value={trigger.triggerType.name}
                />
                {selectTriggerType === TRIGGER_TYPES.CONDITIONAL ? (
                  <Fragment>
                    <Select
                      className={
                        wrongComparison ? classes.selectError : classes.select
                      }
                      value={comparison}
                      onChange={handleComparisonChange}
                      disableUnderline={true}
                      renderValue={comparison => {
                        return (
                          <Box style={{ padding: '0 8px' }}>
                            {comparisonMap[comparison].value}
                          </Box>
                        )
                      }}
                    >
                      {Object.keys(comparisons).map(key => (
                        <MenuItem value={key} key={key}>
                          <div className={classes.comparisonIcon}>
                            {comparisonMap[key].value}
                          </div>
                          <div className={classes.menuItem}>
                            {comparisonMap[key].label}
                          </div>
                        </MenuItem>
                      ))}
                    </Select>

                    <Input
                      type="number"
                      className={
                        wrongConstant
                          ? classes.smallInputError
                          : classes.smallInput
                      }
                      disableUnderline={true}
                      value={
                        isBARating
                          ? triggerConstant > 6
                            ? 6
                            : triggerConstant < 0
                            ? 0
                            : triggerConstant
                          : triggerConstant
                      }
                      onChange={handleConstantChange}
                    />
                  </Fragment>
                ) : null}
              </div>
            </Grid>
          </div>
        </Grid>
        <Grid item xs>
          <div className={classes.line} />
        </Grid>
        {!isAnyElementBelow ? (
          <Grid item xs>
            <Fab
              color="primary"
              aria-label="add"
              onClick={() => handleDrawerToggle(true)}
            >
              <AddIcon />
            </Fab>
          </Grid>
        ) : null}
      </Grid>
    </Fragment>
  )
}

export default injectStyles(styles)(Trigger)
